/**
 * Tela /templates/generate — TH-55174 / M-IAtpl.5.
 *
 * Brief form que dispara POST /templates/generate (TH-55171). Mostra
 * estimativa de custo antes do submit e bloqueia quando o total
 * excederia o orçamento mensal de IA do tenant ou quando o circuit
 * breaker está aberto (ADR-019).
 *
 * Decisões:
 *
 * - Campos do form mapeiam exatamente os 4 campos top-level de
 *   `TemplateGenerationBrief` (Pydantic v2, `extra="forbid"`):
 *   `category`, `style`, `count` e `params`. Sub-campos visuais
 *   (theme, product_a_name, product_b_name, category_hint) entram
 *   em `params` — exatamente como o validator do brief e os prompts
 *   da library (TH-55172) esperam.
 *
 * - 8 categorias seguem o slug canônico de `PromptCategorySlug`
 *   (apps/api/.../schemas/template_generation.py).
 *
 * - 4 estilos: minimalista, complexo, combo_a_b, sazonal. O
 *   validator do brief exige `params['theme']` para `sazonal` e
 *   `params['product_a_name']`/`params['product_b_name']` para
 *   `combo_a_b` — refletido aqui no form (campos condicionais +
 *   validação local antes do submit).
 *
 * - Estimativa de custo: `count × cost_per_draft_brl`. O valor
 *   `cost_per_draft_brl` vem do backend (GET /templates/budget-status,
 *   também criado em TH-55174) — espelha a constante
 *   `_COST_PER_DRAFT_BRL` do service. TH-55173 substituirá pela
 *   estimativa real do provedor; aqui mantemos uma única fonte da
 *   verdade no servidor para evitar drift.
 *
 * - Comparativo com orçamento: `remaining_brl = budget - spent`. Se
 *   `total_estimated > remaining`, o submit fica desabilitado com
 *   aviso explícito. Se `circuit_breaker_state === 'open'`, idem
 *   (mesmo motivo do 422 que o backend retornaria — fail-fast no
 *   cliente).
 *
 * - Navegação pós-submit: navega para `/templates` com toast simples
 *   inline. A galeria de drafts (`/templates/drafts/:id`) é TH-55175
 *   (TODO no momento desta issue) — quando ela existir, basta
 *   redirecionar pra lá com o `session_id` dos draft_ids retornados.
 *
 * - Role gating: a página inteira é restrita a `super_admin` e
 *   `tenant_admin` (mesma regra do `_check_can_generate` no backend).
 *   Outras roles veem mensagem de acesso negado em vez de 403 cego.
 *
 * - i18n: admin não usa i18next ainda (TemplatesPage/NewTemplateDialog
 *   usam strings pt-BR inline). Mantemos o mesmo padrão local até
 *   uma onda de internacionalização global.
 *
 * - Acessibilidade: cada campo tem `<Label htmlFor>`, erros via
 *   `role="alert"`, custo total em região `aria-live="polite"`.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/hooks/useAuth";

// Slugs canônicos (espelham PromptCategorySlug em
// apps/api/.../schemas/template_generation.py).
type Category =
  | "hortifruti"
  | "acougue"
  | "padaria"
  | "mercearia"
  | "bebidas"
  | "promo"
  | "limpeza"
  | "sazonal";

type Style = "minimalista" | "complexo" | "combo_a_b" | "sazonal";

const CATEGORIES: ReadonlyArray<{ value: Category; label: string }> = [
  { value: "hortifruti", label: "Hortifruti" },
  { value: "acougue", label: "Açougue" },
  { value: "padaria", label: "Padaria" },
  { value: "mercearia", label: "Mercearia" },
  { value: "bebidas", label: "Bebidas" },
  { value: "promo", label: "Promo / Oferta" },
  { value: "limpeza", label: "Limpeza" },
  { value: "sazonal", label: "Sazonal" },
];

const STYLES: ReadonlyArray<{ value: Style; label: string; hint: string }> = [
  {
    value: "minimalista",
    label: "Minimalista",
    hint: "Composição limpa, foco no produto.",
  },
  {
    value: "complexo",
    label: "Complexo",
    hint: "Cena rica, ambiente detalhado.",
  },
  {
    value: "combo_a_b",
    label: "Combo A + B",
    hint: "Dois produtos lado a lado — exige nome de A e B.",
  },
  {
    value: "sazonal",
    label: "Sazonal",
    hint: "Tema específico (festa junina, natal, etc.) — exige tema.",
  },
];

// Sugestões para o campo de tema sazonal. Free-text, mas estes são os
// temas exercitados nos pilotos sazonais (art-samples/templates/2026/).
const SAZONAL_THEMES: ReadonlyArray<string> = [
  "festa junina",
  "páscoa",
  "natal",
  "black friday",
  "dia das mães",
  "dia dos pais",
];

interface BudgetStatus {
  budget_monthly_brl: string; // Decimal serializado como string
  spent_current_month_brl: string;
  remaining_brl: string;
  circuit_breaker_state: "closed" | "open" | "half_open";
  cost_per_draft_brl: string;
}

interface GenerateResponse {
  draft_ids: string[];
  estimated_cost_brl: string;
}

async function fetchBudgetStatus(): Promise<BudgetStatus> {
  const res = await fetch("/api/templates/budget-status", {
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as BudgetStatus;
}

interface GeneratePayload {
  category: Category;
  style: Style;
  count: number;
  params: Record<string, string>;
}

async function submitBrief(payload: GeneratePayload): Promise<GenerateResponse> {
  const res = await fetch("/api/templates/generate", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    const detail =
      typeof body.detail === "string"
        ? body.detail
        : `Falha ao enfileirar geração (HTTP ${res.status}).`;
    throw new Error(detail);
  }
  return (await res.json()) as GenerateResponse;
}

function brlFmt(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

// Default visual quando a estimativa do backend ainda não chegou.
// Cliente fica em estado vazio até o budget-status responder — não
// chuta valor pra não enganar o operador sobre o custo real.
const COUNT_DEFAULT = 4;
const COUNT_MIN = 1;
const COUNT_MAX = 8;

export function TemplateGeneratePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const canGenerate =
    user?.role === "super_admin" || user?.role === "tenant_admin";

  const budgetQuery = useQuery({
    queryKey: ["templates", "budget-status"],
    queryFn: fetchBudgetStatus,
    // Refresh sempre que a tela monta — orçamento muda no worker.
    staleTime: 30_000,
    retry: false,
    enabled: canGenerate === true,
  });

  // Form state. Cada item é controlado individualmente — não vale a
  // pena trazer react-hook-form pra um form de 4 campos top-level.
  const [category, setCategory] = useState<Category>("hortifruti");
  const [style, setStyle] = useState<Style>("minimalista");
  const [count, setCount] = useState<number>(COUNT_DEFAULT);
  const [theme, setTheme] = useState<string>("");
  const [productA, setProductA] = useState<string>("");
  const [productB, setProductB] = useState<string>("");
  const [categoryHint, setCategoryHint] = useState<string>("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const requiresTheme = style === "sazonal";
  const requiresCombo = style === "combo_a_b";

  const costPerDraft = budgetQuery.data
    ? Number(budgetQuery.data.cost_per_draft_brl)
    : null;
  const remaining = budgetQuery.data
    ? Number(budgetQuery.data.remaining_brl)
    : null;
  const breakerOpen = budgetQuery.data?.circuit_breaker_state === "open";

  const totalEstimated = useMemo(() => {
    if (costPerDraft == null) return null;
    return Number((costPerDraft * count).toFixed(2));
  }, [costPerDraft, count]);

  const exceedsBudget =
    totalEstimated != null && remaining != null && totalEstimated > remaining;

  // Validação local. Espelha o validator do brief no backend para
  // bloquear cedo (sem trip de rede) — mesma mensagem que o usuário
  // veria caso o submit chegasse ao servidor.
  const formValidationError = useMemo<string | null>(() => {
    if (requiresTheme && !theme.trim()) {
      return "Para estilo sazonal, informe o tema (ex.: 'festa junina').";
    }
    if (requiresCombo && (!productA.trim() || !productB.trim())) {
      return "Para combo A+B, informe os nomes dos dois produtos.";
    }
    return null;
  }, [requiresTheme, requiresCombo, theme, productA, productB]);

  const submitDisabled =
    submitting ||
    breakerOpen ||
    exceedsBudget ||
    formValidationError != null ||
    !budgetQuery.data;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitDisabled) return;

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    const params: Record<string, string> = {};
    if (requiresTheme) params.theme = theme.trim();
    if (requiresCombo) {
      params.product_a_name = productA.trim();
      params.product_b_name = productB.trim();
    }
    if (categoryHint.trim()) {
      params.category_hint = categoryHint.trim();
    }

    try {
      const resp = await submitBrief({
        category,
        style,
        count,
        params,
      });
      setSuccess(
        `${resp.draft_ids.length} draft(s) enfileirado(s). Custo estimado: ${brlFmt(
          Number(resp.estimated_cost_brl),
        )}.`,
      );
      // Recarrega o budget — gasto foi reservado server-side.
      void budgetQuery.refetch();
      // Galeria de drafts (TH-55175) — redireciona para acompanhar o
      // pipeline. Polling automático lá pega o status pending→ready.
      window.setTimeout(() => {
        navigate({ to: "/templates/drafts" });
      }, 1500);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha ao enfileirar geração.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (user && !canGenerate) {
    return (
      <RequireAuth>
        <AppLayout
          breadcrumb="OPERAÇÃO / TEMPLATES / GERAR"
          title="Acesso negado"
        >
          <div className="mx-auto max-w-3xl">
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Apenas super_admin ou tenant_admin podem gerar templates via IA.
            </p>
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / TEMPLATES / GERAR"
        title="Gerar templates com IA"
      >
        <div className="mx-auto max-w-3xl space-y-6">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Brief curto, fundo IA gerado pelo provedor configurado. Os
            drafts ficam pendentes de aprovação na galeria.
          </p>

          {/* Painel de orçamento — mostra antes do form pra deixar a
              comparação visual de custo óbvia para o operador. */}
          <section
            className="rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-card)] p-4"
            aria-labelledby="budget-heading"
            data-testid="budget-panel"
          >
            <h2
              id="budget-heading"
              className="mb-3 text-sm font-medium text-[var(--color-foreground)]"
            >
              Orçamento mensal de IA
            </h2>
            {budgetQuery.isLoading ? (
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Carregando…
              </p>
            ) : budgetQuery.error || !budgetQuery.data ? (
              <p
                role="alert"
                className="text-sm text-[var(--color-destructive)]"
              >
                Não foi possível carregar o orçamento. Tente recarregar.
              </p>
            ) : (
              <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-[var(--color-muted-foreground)]">
                    Orçamento
                  </dt>
                  <dd
                    className="font-mono tabular-nums"
                    data-testid="budget-total"
                  >
                    {brlFmt(Number(budgetQuery.data.budget_monthly_brl))}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--color-muted-foreground)]">
                    Gasto este mês
                  </dt>
                  <dd
                    className="font-mono tabular-nums"
                    data-testid="budget-spent"
                  >
                    {brlFmt(Number(budgetQuery.data.spent_current_month_brl))}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--color-muted-foreground)]">
                    Restante
                  </dt>
                  <dd
                    className="font-mono tabular-nums"
                    data-testid="budget-remaining"
                  >
                    {brlFmt(Number(budgetQuery.data.remaining_brl))}
                  </dd>
                </div>
              </dl>
            )}
            {breakerOpen ? (
              <div
                role="alert"
                data-testid="circuit-breaker-open"
                className="mt-3 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                Orçamento de IA atingido — circuit breaker aberto. Geração
                bloqueada até o reset mensal ou aumento do limite.
              </div>
            ) : null}
          </section>

          <form
            onSubmit={(e) => void handleSubmit(e)}
            className="space-y-5"
            data-testid="brief-form"
            aria-describedby="cost-summary"
          >
            <div className="space-y-1.5">
              <Label htmlFor="brief-category">Categoria</Label>
              <Select
                id="brief-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as Category)}
                disabled={submitting}
                data-testid="brief-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Define o departamento da loja. Cada categoria tem um prompt
                versionado próprio (TH-55172).
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="brief-style">Estilo</Label>
              <Select
                id="brief-style"
                value={style}
                onChange={(e) => setStyle(e.target.value as Style)}
                disabled={submitting}
                data-testid="brief-style"
                aria-describedby="brief-style-hint"
              >
                {STYLES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <p
                id="brief-style-hint"
                className="text-xs text-[var(--color-muted-foreground)]"
              >
                {STYLES.find((s) => s.value === style)?.hint}
              </p>
            </div>

            {requiresTheme ? (
              <div className="space-y-1.5">
                <Label htmlFor="brief-theme">Tema sazonal</Label>
                <Input
                  id="brief-theme"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  list="brief-theme-suggestions"
                  placeholder="Ex.: festa junina"
                  disabled={submitting}
                  required
                  maxLength={80}
                  data-testid="brief-theme"
                />
                <datalist id="brief-theme-suggestions">
                  {SAZONAL_THEMES.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
            ) : null}

            {requiresCombo ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="brief-product-a">Produto A</Label>
                  <Input
                    id="brief-product-a"
                    value={productA}
                    onChange={(e) => setProductA(e.target.value)}
                    placeholder="Ex.: arroz tipo 1"
                    disabled={submitting}
                    required
                    maxLength={120}
                    data-testid="brief-product-a"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="brief-product-b">Produto B</Label>
                  <Input
                    id="brief-product-b"
                    value={productB}
                    onChange={(e) => setProductB(e.target.value)}
                    placeholder="Ex.: feijão carioca"
                    disabled={submitting}
                    required
                    maxLength={120}
                    data-testid="brief-product-b"
                  />
                </div>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="brief-hint">Brief adicional (opcional)</Label>
              <Input
                id="brief-hint"
                value={categoryHint}
                onChange={(e) => setCategoryHint(e.target.value)}
                placeholder="Pista visual extra — ex.: 'forno a lenha rústico'."
                disabled={submitting}
                maxLength={200}
                data-testid="brief-hint"
              />
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Texto livre, anexado como params['category_hint'] ao prompt.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="brief-count">
                Quantidade de drafts ({COUNT_MIN}–{COUNT_MAX})
              </Label>
              <Input
                id="brief-count"
                type="number"
                min={COUNT_MIN}
                max={COUNT_MAX}
                value={count}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (Number.isFinite(next)) {
                    setCount(
                      Math.min(COUNT_MAX, Math.max(COUNT_MIN, Math.trunc(next))),
                    );
                  }
                }}
                disabled={submitting}
                data-testid="brief-count"
              />
              <p className="text-xs text-[var(--color-muted-foreground)]">
                Cada draft é uma variação independente do mesmo brief.
              </p>
            </div>

            {/* Resumo de custo — sempre visível mesmo durante load,
                pra o operador saber o que vai pagar antes de submeter. */}
            <section
              id="cost-summary"
              aria-live="polite"
              data-testid="cost-summary"
              className="rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-card)] p-4"
            >
              <h2 className="mb-2 text-sm font-medium text-[var(--color-foreground)]">
                Custo estimado
              </h2>
              {costPerDraft == null || totalEstimated == null ? (
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  Aguardando orçamento…
                </p>
              ) : (
                <div className="space-y-1 text-sm">
                  <p>
                    <span className="text-[var(--color-muted-foreground)]">
                      Por draft:{" "}
                    </span>
                    <span
                      className="font-mono tabular-nums"
                      data-testid="cost-per-draft"
                    >
                      {brlFmt(costPerDraft)}
                    </span>
                  </p>
                  <p>
                    <span className="text-[var(--color-muted-foreground)]">
                      Total ({count} × {brlFmt(costPerDraft)}):{" "}
                    </span>
                    <span
                      className="font-mono tabular-nums font-medium"
                      data-testid="cost-total"
                    >
                      {brlFmt(totalEstimated)}
                    </span>
                  </p>
                  {remaining != null ? (
                    <p>
                      <span className="text-[var(--color-muted-foreground)]">
                        Restante após:{" "}
                      </span>
                      <span
                        className="font-mono tabular-nums"
                        data-testid="cost-remaining-after"
                      >
                        {brlFmt(remaining - totalEstimated)}
                      </span>
                    </p>
                  ) : null}
                </div>
              )}
              {exceedsBudget ? (
                <div
                  role="alert"
                  data-testid="cost-exceeds-budget"
                  className="mt-3 rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
                >
                  Orçamento mensal de IA insuficiente. Restante:{" "}
                  <strong className="font-mono tabular-nums">
                    {brlFmt(remaining ?? 0)}
                  </strong>
                  . Solicitado:{" "}
                  <strong className="font-mono tabular-nums">
                    {brlFmt(totalEstimated ?? 0)}
                  </strong>
                  . Reduza a quantidade de drafts ou peça aumento de limite.
                </div>
              ) : null}
            </section>

            {formValidationError ? (
              <p
                role="alert"
                data-testid="brief-validation-error"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                {formValidationError}
              </p>
            ) : null}

            {error ? (
              <p
                role="alert"
                data-testid="brief-submit-error"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                {error}
              </p>
            ) : null}

            {success ? (
              <p
                role="status"
                data-testid="brief-success"
                className="rounded-md border border-[var(--color-success)] bg-[var(--color-success)]/10 px-3 py-2 text-sm text-[var(--color-success)]"
              >
                {success}
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate({ to: "/templates" })}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={submitDisabled}
                data-testid="brief-submit"
              >
                {submitting ? "Enfileirando…" : "Gerar drafts"}
              </Button>
            </div>
          </form>

          {budgetQuery.data ? (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Custo unitário hoje:{" "}
              <Badge variant="muted">
                {brlFmt(Number(budgetQuery.data.cost_per_draft_brl))}
              </Badge>{" "}
              — definido pelo provedor configurado (ADR-019). Pode variar
              quando TH-55173 trouxer o custo real do provedor.
            </p>
          ) : null}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
